import { useProduct } from '@shopify/hydrogen-react';
import { SelectedOptions } from '@shopify/hydrogen-react/ProductProvider';
import { Product, ProductVariant } from '@shopify/hydrogen-react/storefront-api-types';
import classNames from 'classnames';
import React, { useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { findVariantByOptions } from './functions';
import ProductPriceFormatted from './ProductPriceFormatted';
import OptionsWrapper, { ProductVariantOptions } from './variations/OptionsWrapper';
import ViewedProductContext from './ViewedProductContext';
import OptimizedImage from '../../components/ui/OptimizedImage';
import { useStoreState } from '../../store';
import { elevarSelectItem } from '../../utils/elevar';
import { isBundleProduct } from '../../utils/storefront/exclusiveProducts';

interface Props {
  className?: string;
  disableClick?: boolean;
  flat?: boolean;
  fullHeight?: boolean;
  index?: number;
  optionsShow?: boolean;
  size?: 'small' | 'regular';
  withUrlParamsLink?: boolean;
}

const ProductCard: React.FC<Props> = ({
  className,
  disableClick = false,
  flat,
  fullHeight,
  index = 0,
  optionsShow = true,
  size = 'regular',
  withUrlParamsLink = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { elevarCustomerData } = useStoreState(state => state.analytics);
  const productVariantContext = useContext(ViewedProductContext);
  const addProduct = productVariantContext?.addProduct;

  const { product, selectedVariant, setSelectedVariant, setSelectedOptions, selectedOptions } =
    useProduct();

  const fromUrl = location.pathname + location.search;
  const productSearch = withUrlParamsLink
    ? new URLSearchParams(selectedOptions as SelectedOptions).toString()
    : '';
  const productTo = `/product/${product?.handle}${productSearch ? `?${productSearch}` : ''}`;
  const productNavState = { from: fromUrl };

  useEffect(() => {
    setSelectedVariant(
      findVariantByOptions(product as Product, selectedOptions as SelectedOptions)
    );
  }, [selectedOptions]);

  useEffect(() => {
    selectedVariant &&
      !!addProduct &&
      index < 10 &&
      addProduct(
        { productVariant: selectedVariant as ProductVariant, product: product as Product },
        index
      );
  }, [selectedVariant]);

  if (!product) {
    return null;
  }

  return (
    <div
      className={classNames(
        {
          pc: true,
          flat: flat,
          fh: fullHeight,
        },
        size,
        className
      )}
    >
      <div className="pc-top">
        <OptimizedImage
          className="pc-top-image"
          url={selectedVariant?.image?.url}
          viewportSize={{ desktop: 30, desktopSmall: 30, tablet: 50 }}
          onClick={() => {
            if (!disableClick) {
              navigate(productTo, { state: productNavState });
              elevarSelectItem(
                elevarCustomerData,
                product as Product,
                selectedVariant as ProductVariant,
                index
              );
            }
          }}
        />

        {process.env.REACT_APP_FEATURE_PRODUCT_TAGS === 'true' &&
          product &&
          product.tags?.includes('New Arrivals') && <div className="pc-top-new">New</div>}
      </div>

      {!!product?.options?.length && optionsShow && !isBundleProduct(product as Product) && (
        <OptionsWrapper
          onSelectedOptions={selectedOptions => setSelectedOptions(selectedOptions)}
          selectedOptions={selectedOptions as SelectedOptions}
          selectedVariant={selectedVariant as ProductVariant}
          product={product as Product}
          showOnly={ProductVariantOptions.COLOR}
          withInfo={false}
        />
      )}
      <Link className="pc-title heading h5" to={productTo} state={productNavState}>
        {product?.title}
      </Link>
      <div className="pc-price">
        <ProductPriceFormatted data={selectedVariant as ProductVariant} />
      </div>
    </div>
  );
};

export default ProductCard;
